/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-12 15:40:50                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-25 16:02:48                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * DetailsDrawer: display details drawer
*/

/* Imports */
import { Button, Card, CardBody, CardFooter, Typography } from "@material-tailwind/react";
import { Modal } from "@mui/material";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { t } from "i18next";
/***/

/* Components */
import ExportFieldPicker from "./ExportFieldPicker";
/***/

/* Services */
import {
  columnKey,
  readExcludedColumns,
  selectDescriptor,
  writeExcludedColumns
} from "../../services/export/exportFieldSelection";
/***/

import Loading from "../loading/Loading";
import { SearchOptions, SearchQuery, SortDirection, searchWithFilters } from "../../services/api/search";
import { downloadXlsx } from "../../services/export/export";

/* Styles */
import "./ExportModal.scss";
/***/

/**
* ExportModal: confirm, then download the rows the list view is displaying.
*
* The promise this dialog makes is "you get what you are looking at", so it needs every input that
* shapes the screen — not just the filters:
*   - `order` is what the user sorted the table by. Omitting it used to hand back a spreadsheet in
*     the API's default order, silently disagreeing with the table above it.
*   - `extraQueries` is the scoping a parent view adds on top of the filter drawer (a customer's
*     interventions add `customer.id`). Without it the export ignored the customer and returned the
*     whole tenant.
*   - `rows` is for views that hold their whole dataset in memory and have no search endpoint to
*     call (the Users tabs). When present the API is skipped entirely.
*
* The confirmation copy is one translated key, not a per-view sentence. It used to arrive as a
* `modalContent` prop, which meant seven hardcoded English sentences no locale could reach. The
* entity is deliberately not interpolated into it: the page title already names what is being
* exported, and dropping a noun into a sentence across nine languages invites the gender, case and
* capitalisation bugs the i18n audit exists to prevent.
*
* @param filters - active filter values, mapped to queries by getQueryFromFilter
* @param descriptor - column mapping for the workbook
* @param fileName - downloaded file name
* @param entityName - search entity, e.g. "customers"
* @param open - whether the dialog is shown
* @param getQueryFromFilter - maps filters to search queries
* @param onClose - close callback
* @param order - the table's active sort, forwarded to the search
* @param extraQueries - scoping queries the parent view applies on top of the filters
* @param rows - export these rows directly instead of searching
*/
export default function ExportModal<T>({
  filters,
  descriptor,
  fileName,
  entityName,
  open,
  getQueryFromFilter,
  onClose,
  order,
  extraQueries,
  rows
}: {
  filters?: Record<string, any>;
  descriptor: any;
  fileName: string;
  entityName: string;
  open: boolean;
  /** Omit in `rows` mode: a view with its data in hand has no filters to translate. */
  getQueryFromFilter?: (filters: any) => SearchQuery[];
  onClose: () => void;
  order?: { by: string; direction: SortDirection };
  extraQueries?: SearchQuery[];
  rows?: T[];
}) {
  const [downLoad, setDownLoad] = useState<boolean>(false);

  /**
   * Les colonnes retenues, initialisées depuis le choix mémorisé pour cette entité.
   *
   * Ce qui est stocké, ce sont les colonnes refusées : une colonne ajoutée au descripteur après
   * coup arrive donc cochée, au lieu de rester invisible pour qui avait déjà exporté une fois.
   * L'initialiseur paresseux fait la lecture une seule fois — la modale n'est montée qu'à
   * l'ouverture, mais elle se rend à chaque frappe d'état.
   */
  const [selectedFields, setSelectedFields] = useState<string[]>(() => {
    const excluded = readExcludedColumns(entityName);
    return descriptor
      .map(columnKey)
      .filter((key: string) => !excluded.includes(key));
  });

  const selectedDescriptor = useMemo(
    () => selectDescriptor(descriptor, selectedFields),
    [descriptor, selectedFields]
  );

  // Un classeur sans colonne ne contient rien : on bloque plutôt que de livrer un fichier vide.
  const canDownload = selectedFields.length > 0;

  /**
  * onFieldsChange: mémorise le choix au moment où il est fait.
  *
  * À la sélection plutôt qu'au téléchargement : quelqu'un qui ajuste ses colonnes puis annule a
  * quand même exprimé une préférence, et la retrouver à la réouverture est le comportement attendu.
  */
  const onFieldsChange = (next: string[]) => {
    setSelectedFields(next);
    const kept = new Set(next);
    writeExcludedColumns(
      entityName,
      descriptor.map(columnKey).filter((key: string) => !kept.has(key))
    );
  };

  /**
  * resolveRows: the rows to write, either already in hand or fetched.
  *
  * `throwOnError` is essential rather than defensive: searchWithFilters resolves to an empty page
  * on failure by default, so without it a failed export looks exactly like an empty one and the
  * catch below could never run.
  */
  const resolveRows = (): Promise<T[]> => {
    if (rows) return Promise.resolve(rows);

    const searchOptions: SearchOptions = {
      queries: [...(getQueryFromFilter?.(filters) ?? []), ...(extraQueries ?? [])],
      // Only send `order` when the caller has one; an order with an undefined `by` is not the same
      // as no order at all as far as the API is concerned.
      ...(order ? { order } : {})
    };

    // `isAdmin` is deliberately left unset: the list views themselves always read the `users`
    // space, so the export stays consistent with the list it sits above.
    return searchWithFilters<T>(entityName, searchOptions, true, undefined, true)
      .then((ret) => ret.data);
  };

  const downloadWoReport = () => {
    if (!canDownload) return;

    setDownLoad(true);
    resolveRows()
      .then((data) => {
        // Inside the `then` so a workbook that fails to build is reported too, rather than
        // disappearing into the `finally` that closes the dialog.
        downloadXlsx<T>(fileName, data, selectedDescriptor);
        toast.success(t("common:export.success"));
      })
      .catch((err) => {
        console.error(err);
        toast.error(t("common:export.error"));
      })
      .finally(() => {
        setDownLoad(false);
        onClose();
      });
  };

  return (

    <Modal
      open={open}
      onClose={onClose}
      className="te-modal"
      aria-labelledby="te-modal"
      aria-describedby="te-modal">
      <Card className="te-modal-content  rounded-lg">
        {/* Icon-only, like the trigger: it needs a name of its own to be reachable. */}
        <button className="close-button" aria-label={t("common:close")}
          onClick={() => onClose()} disabled={downLoad}>
          <XMarkIcon className="w-4" />
        </button>
        <CardBody>
          <div className="flex items-center gap-3 mb-5">
            <div className="rounded-full border p-2 bg-light-green-200">
              <CalendarIcon className="w-7 text-green-700" />
            </div>
            <Typography variant="h5">
              {t("common:export.title")}
            </Typography>
          </div>

          <div className="field flex flex-row items-center gap-2">
            <Typography>
              {downLoad ? t("common:export.inProgress") : t("common:export.confirm")}
            </Typography>

          </div>

          {/* Masqué pendant le téléchargement : la liste n'est plus actionnable et laisser 29 cases
            * grisées sous un message d'attente n'apporte rien. */}
          {!downLoad &&
            <div className="mt-4">
              <ExportFieldPicker
                descriptor={descriptor}
                selected={selectedFields}
                onChange={onFieldsChange}
                disabled={downLoad}
              />
            </div>
          }
        </CardBody>
        <CardFooter className="pt-0 flex gap-2 justify-end">


          {downLoad ?
            <Loading />
            :
            <>
              <Button variant="outlined"
                onClick={onClose}>
                {t("common:cancel")}
              </Button>
              <Button color="blue"
                disabled={!canDownload}
                onClick={() => downloadWoReport()}>
                {t("common:download")}
              </Button>
            </>
          }
        </CardFooter>
      </Card>
    </Modal>
  );
}
/***/
